/**
 * The anatomy every record body shares: an identity header, fact lists,
 * related rows, and the action row. A body composes these and nothing
 * else, so twelve kinds read as one panel.
 */
import type { UseQueryResult } from '@tanstack/react-query';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { ErrorState, Icon, KeyValue, Typography, type KeyValueItem } from '../../ui/finixt.js';
import { type RecordRef } from './record.js';
import { useRecordPage } from './record-page.js';
import { RecordNotFound } from './RecordPanel.js';

/** The three states of a read, then the body. A 404 is "not found", not an error. */
export function RecordAsync<T>({
  query,
  record,
  children,
}: {
  query: UseQueryResult<T>;
  record: RecordRef;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <LoadingBlock />;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return <RecordNotFound record={record} />;
    }
    return (
      <ErrorBlock message={query.error instanceof Error ? query.error.message : 'Failed to load'} />
    );
  }
  return <>{children(query.data)}</>;
}

/** A list read that found no row with the id: the same "not found" as a 404. */
export function RecordMissing({ record }: { record: RecordRef }) {
  return <RecordNotFound record={record} />;
}

export function RecordIdentity({
  avatar,
  eyebrow,
  name,
  subtitle,
}: {
  /** An IconTile for a record, a round Avatar for a person. */
  avatar: ReactNode;
  /** Badges and mono labels, after the kind. */
  eyebrow?: ReactNode;
  name: ReactNode;
  subtitle?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
      <div style={{ flexShrink: 0 }}>{avatar}</div>
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        {eyebrow ? (
          <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
            {eyebrow}
          </div>
        ) : null}
        <div
          style={{
            marginTop: eyebrow ? 5 : 0,
            fontSize: 'var(--text-title)',
            fontWeight: 'var(--weight-bold)',
            letterSpacing: 'var(--tracking-title)',
            color: 'var(--text-strong)',
            overflowWrap: 'anywhere',
          }}
        >
          {name}
        </div>
        {subtitle ? (
          <div style={{ marginTop: 2, fontSize: 'var(--text-meta-lg)', color: 'var(--text-soft)' }}>
            {subtitle}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function RecordSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <Typography variant="eyebrow">{label}</Typography>
      {children}
    </div>
  );
}

export function RecordFacts({ items }: { items: KeyValueItem[] }) {
  return <KeyValue items={items} dense labelWidth={104} />;
}

export interface RelatedRow {
  icon: string;
  label: ReactNode;
  /** A mono count or hint at the right edge. */
  meta?: ReactNode;
  onClick?: (() => void) | undefined;
}

/** Rows that lead somewhere: a push into a sub-panel, or a navigation. */
export function RecordRelated({ rows }: { rows: RelatedRow[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((row, index) => {
        const inner = (
          <>
            <Icon name={row.icon} size={16} color="var(--text-soft)" />
            <span
              style={{
                flex: '1 1 0%',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 'var(--text-body-sm)',
                color: 'var(--text-strong)',
              }}
            >
              {row.label}
            </span>
            {row.meta !== undefined ? (
              <span
                className="mono"
                style={{ fontSize: 'var(--text-meta)', color: 'var(--text-soft)' }}
              >
                {row.meta}
              </span>
            ) : null}
            {row.onClick ? <Icon name="chevron_right" size={14} color="var(--text-ghost)" /> : null}
          </>
        );
        const style: CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          width: '100%',
          padding: '7px 0',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          borderTop: index === 0 ? 'none' : '1px solid var(--border-divider)',
          cursor: row.onClick ? 'pointer' : 'default',
          font: 'inherit',
          color: 'inherit',
        };
        return row.onClick ? (
          <button key={index} type="button" onClick={row.onClick} style={style}>
            {inner}
          </button>
        ) : (
          <div key={index} style={style}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

/** The action row at the foot of a body. One primary at most. */
export function RecordActions({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        paddingTop: 11,
        borderTop: '1px solid var(--border-hairline)',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Open the record's full page (D7). Navigating drops `?record=`, which closes
 * the panel. The page is `record`'s from the one map a double-click and
 * Shift+Enter read too (optum-agent Feature 87, D18), or `to` where the map
 * cannot name it.
 */
export function RecordOpen({
  record,
  to,
  label = 'Open',
}: {
  record?: RecordRef | undefined;
  to?: string | undefined;
  label?: string;
}) {
  const navigate = useNavigate();
  const pageOf = useRecordPage();
  const target = to ?? (record ? pageOf(record) : null);
  if (!target) return null;
  return (
    <Button variant="primary" icon="open_in_full" onClick={() => navigate(target)}>
      {label}
    </Button>
  );
}

/** A body's whole column: identity first, actions last, 16px between sections. */
export function RecordColumn({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{children}</div>;
}

/** An inline failure or warning the body wants to show in full. */
export function RecordMessage({
  tone,
  children,
}: {
  tone: 'danger' | 'warn';
  children: ReactNode;
}) {
  return (
    <pre
      className="mono"
      style={{
        margin: 0,
        padding: '9px 11px',
        borderRadius: 'var(--radius-input)',
        background: tone === 'danger' ? 'var(--danger-tint)' : 'var(--warn-tint)',
        color: tone === 'danger' ? 'var(--danger)' : 'var(--warn-ink)',
        fontSize: 'var(--text-meta-lg)',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </pre>
  );
}

/** For a kind with no read of its own and a failed list. */
export function RecordListError({ message }: { message: string }) {
  return <ErrorState kind="error" compact title="Could not load" body={message} />;
}
